import { SettingsList } from './SettingsList';
import { ApprovalDeviceIdentity } from '../devices/DeviceIdentity';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { approveJobBatch, batchApprovalOptions, getAgentBatches, rejectJobBatch } from '../lib/api';
import { signWithKey } from '../lib/webauthn';
import { absolute } from '../lib/time';
import { JobStateBadge, ReviewList } from '../operations/meta';
import { useScope } from '../scope/scope';
import { Button } from '../ui/Button';
import { useToast } from '../ui/toast';

/**
 * Agents' device jobs (WRMM-397): what an agent asked to run, exactly, on which computers. One key touch approves the
 * whole batch; each computer checks that approval itself. Only the owner who issued the agent's credential can approve.
 */
export function AgentBatches() {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const key = ['scope', mspId, 'agent-batches'];
  const list = useQuery({ queryKey: key, queryFn: ({ signal }) => getAgentBatches(mspId, signal), refetchInterval: 15_000 });
  const refresh = () => void client.invalidateQueries({ queryKey: key });
  const approve = useMutation({
    mutationFn: async (id: string) => approveJobBatch(mspId, id, await signWithKey(await batchApprovalOptions(mspId, id))),
    onSuccess: () => { refresh(); toast({ title: 'Batch approved', body: 'Each computer runs it when it next checks in.' }); },
    onError: () => toast({ tone: 'warning', title: 'The batch wasn’t approved', body: 'Only the owner who issued this agent’s credential can approve it. Touch your key when asked.' }),
  });
  const reject = useMutation({
    mutationFn: (id: string) => rejectJobBatch(mspId, id, 'Rejected in Agent access'),
    onSuccess: refresh,
    onError: () => toast({ tone: 'warning', title: 'The batch couldn’t be rejected', body: 'Refresh and try again.' }),
  });
  const busy = approve.isPending || reject.isPending;
  return <section className="space-y-2" aria-label="Agent device jobs">
    <p className="text-[12.5px] text-ink-3">An agent with “Ask to run scripts and runbooks” asks for one script or runbook on the computers you named. Nothing that changes a computer runs until you approve the batch with your key; each computer checks that approval itself.</p>
    {list.isPending && <p className="text-[13px] text-ink-3">Loading agent jobs…</p>}
    {list.isError && <p role="alert" className="text-[13px] text-ink-3">Agent jobs couldn’t be loaded.</p>}
    {list.data?.length === 0 && <p className="text-[13px] text-ink-3">No agent jobs in the last day.</p>}
    <SettingsList id="agent-jobs" title="Agent device jobs" items={list.data ?? []} newest={b => b.requestedAt}>{visible => <ul className="divide-y divide-line rounded-lg border border-line">
      {visible.map(b => <li key={b.id} className="space-y-1.5 px-3 py-2 text-[13px]">
        <p className="font-medium">{b.agentName} · {b.title} · {b.size} {b.size === 1 ? 'computer' : 'computers'}</p>
        <p className="text-ink-2">{b.reason}</p>
        <p className="text-[12.5px] text-ink-3">Asked {absolute(b.requestedAt)} · runs as {b.runAs === 'system' ? 'SYSTEM/root' : 'the signed-in user'}
          {b.operation === 'script' ? ` · ${b.shell}` : ''}</p>
        <p className="break-all font-mono text-[12px] text-ink-2">SHA-256 {b.scriptSha256}</p>
        {b.allowRestart && <p role="alert" className="font-medium text-attention">This may restart or shut down these computers.</p>}
        {b.runbook && <div>
          <p>Runbook: {b.runbook.title} · <span className="font-mono">{b.runbook.id}</span> v{b.runbook.version}{b.runbook.effect === 'read' ? ' · read-only' : ''}</p>
          <p className="font-mono text-[12px] text-ink-2">Parameters: {Object.keys(b.runbook.parameters).length
            ? Object.entries(b.runbook.parameters).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(' ') : 'none'}</p>
        </div>}
        {b.script !== null && <details open={b.pending}>
          <summary className="cursor-pointer text-[12.5px] text-ink-2">The exact script</summary>
          <pre className="mt-1 max-h-72 overflow-auto rounded bg-panel px-2 py-1.5 font-mono text-[12px]">{b.script}</pre>
        </details>}
        <ReviewList findings={b.review} compact />
        <ul className="space-y-0.5">
          {b.devices.map(d => <li key={d.jobId} className="flex flex-wrap items-start gap-2">
            <JobStateBadge state={d.state} />
            <ApprovalDeviceIdentity device={{ id: d.deviceId, title: d.displayName || d.hostname || 'Removed computer', hostname: d.hostname }} />
            <bdi dir="auto" className="text-ink-3">{[d.customerName, d.siteName].filter(Boolean).join(' / ')}</bdi>
            {d.exitCode !== null && <span className="text-ink-3">exit {d.exitCode}</span>}
          </li>)}
        </ul>
        {b.pending && !b.canApprove && <p className="text-ink-3">Only the owner who issued this agent’s credential can approve it.</p>}
        {b.pending && <div className="flex gap-2">
          {b.canApprove && <Button size="sm" disabled={busy} onClick={() => approve.mutate(b.id)}>{approve.isPending ? 'Touch your key…' : 'Approve with your key'}</Button>}
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => reject.mutate(b.id)}>Reject</Button>
        </div>}
      </li>)}
    </ul>}</SettingsList>
  </section>;
}
